// ─────────────────────────────────────────────────────────────────────────────
// deltaCallRow.ts — WHICH CALL THE OWNER PRESSED. THE STORE, NOT THE COMPONENT.
//
// The Calls tab's list (`sales.cold_call_tracker`) and the call gate are two
// renders on one screen. The row the owner pressed is the ONE fact they share,
// so it lives here — the same shape as `lib/deltaSelection` — and neither holds
// a copy of it.
//
// ── THE IDS ARE THE ROW'S OWN, NEVER MINTED ────────────────────────────────
// `sales_task_id` and `call_id` are ISSUED by the producer on the list row
// (`core/delta_board.calls_slots`, APP_CALLS_CONTRACT §1a) and the gate sends
// back exactly that pair. Nothing in this file makes either up: a row that does
// not carry `company_id` and `sales_task_id` is not a call the gate can answer,
// so it is not pressable at all.
//
// ── A `done` ROW IS NOT PRESSABLE ──────────────────────────────────────────
// It carries no `call_id` (contract §1a) and there is nothing left to answer.
//
// ── THE ROW BELONGS TO A COMPANY ───────────────────────────────────────────
// `companyId` is the stamp `artefactFor` checks, so a row pressed for one
// company cannot be answered while the switcher shows another.
// ─────────────────────────────────────────────────────────────────────────────

import { CompanyArtefact, select, setSelection } from './deltaSelection';

export interface CallRow extends CompanyArtefact {
  companyId: string;
  slug: string | null;
  salesTaskId: string;
  /** Absent on a row the producer issued none for — sent back only when present. */
  callId: string | null;
  /** The row's own words, for the gate's heading. Never a name made from the slug. */
  who: string;
  when: string;
}

const text = (v: unknown): string => (v == null ? '' : String(v).trim());

/** The pressable row, or `null`. Own-property reads — a board row is untrusted. */
export function callRowOf(task: unknown): CallRow | null {
  if (!task || typeof task !== 'object') return null;
  const own = (k: string) =>
    Object.prototype.hasOwnProperty.call(task, k) ? text((task as Record<string, unknown>)[k]) : '';
  if (own('state') === 'done') return null;
  const companyId = own('company_id');
  const salesTaskId = own('sales_task_id');
  if (!companyId || !salesTaskId) return null;
  return {
    companyId,
    slug: own('slug') || null,
    salesTaskId,
    callId: own('call_id') || null,
    who: own('who'),
    when: own('when'),
  };
}

type Listener = (r: CallRow | null) => void;

let current: CallRow | null = null;
const listeners = new Set<Listener>();

export function getCallRow(): CallRow | null {
  return current;
}

export function setCallRow(next: CallRow | null): void {
  current = next;
  listeners.forEach((fn) => fn(current));
}

export function subscribeCallRow(fn: Listener): () => void {
  listeners.add(fn);
  fn(current);
  return () => {
    listeners.delete(fn);
  };
}

/** THE PRESS. Selects the row's company — the same selection every Delta tab
 *  reads, so the script beside the list is that company's — and hands the gate
 *  THIS row. Selection first: the gate checks the row against it. */
export function pressCallRow(row: CallRow): void {
  setSelection(select(row.companyId));
  setCallRow(row);
}

/** Test seam ONLY. Never a production handle. */
export function __resetCallRowForTest(): void {
  current = null;
  listeners.clear();
}
